/* eslint-disable node/no-extraneous-import */
import { History } from "history";
import { PAGINATION_PAGE_SIZE } from "./Pagination.types";

export class PaginationController {
  private static instance: PaginationController;

  /**
   * getRange
   * @private
   * @param {number} start
   * @param {number} end
   * @memberof PaginationController
   */
  private getRange(start: number, end: number) {
    return Array(end - start + 1)
      .fill(0)
      .map((v, i) => i + start);
  }

  /**
   * triggerPageScroll
   * @param {History<unknown>} history
   * @memberof PaginationController
   */
  public triggerPageScroll(history: History<unknown>): void {
    history.replace("/");
  }

  /**
   * getNumberOfPages
   * @param {number} totalCount
   * @return {*}  {number}
   * @memberof PaginationController
   */
  public getNumberOfPages(totalCount: number): number {
    return Math.ceil(totalCount / PAGINATION_PAGE_SIZE);
  }

  /**
   * getPaginationCollection
   * @param {number} currentPage
   * @param {number} maxPages
   * @return {*}  {(Array<string | number>)}
   * @memberof PaginationController
   */
  public getPaginationCollection(
    currentPage: number,
    maxPages: number
  ): Array<string | number> {
    let delta: number;

    if (maxPages <= 7) {
      // delta === 7: [1 2 3 4 5 6 7]
      delta = 7;
    } else {
      // delta === 2: [1 ... 4 5 6 ... 10]
      // delta === 4: [1 2 3 4 5 ... 10]
      delta = currentPage > 4 && currentPage < maxPages - 3 ? 2 : 4;
    }

    const range = {
      start: Math.round(currentPage - delta / 2),
      end: Math.round(currentPage + delta / 2),
    };

    // istanbul ignore next
    if (range.start - 1 === 1 || range.end + 1 === maxPages) {
      range.start += 1;
      range.end += 1;
    }

    let pages: Array<string | number> =
      currentPage > delta
        ? this.getRange(
            Math.min(range.start, maxPages - delta),
            Math.min(range.end, maxPages)
          )
        : this.getRange(1, Math.min(maxPages, delta + 1));

    const withDots = /* istanbul ignore next */ (
      value: number,
      pair: (string | number)[]
    ) => (pages.length + 1 !== maxPages ? pair : [value]);

    if (pages[0] !== 1) {
      pages = withDots(1, [1, "..."]).concat(pages);
    }

    if (pages[pages.length - 1] < maxPages) {
      pages = pages.concat(withDots(maxPages, ["...", maxPages]));
    }

    return pages;
  }

  public static getInstance(): PaginationController {
    if (!PaginationController.instance) {
      PaginationController.instance = new PaginationController();
    }

    return PaginationController.instance;
  }
}
